import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs'
import {
  HeadMetatags,
  Container,
  InnerPageLayout,
} from 'routes/components/Layout'
import {
  ScenarioTemplatesProvider,
  useScenarioTemplates,
} from './context/ScenariosContext'
import { useScenarios } from './hooks/useScenarios'
import { getScenariosTagsDataWithCount } from 'routes/Scenarios/helpers'
import { ScenarioTemplateCompact } from 'routes/Scenarios/helpers/types'
import { ScenariosHero } from './components/ScenariosHero'
import { ScenariosSearch } from './components/ScenariosSearch'
import { ScenariosTags } from './components/ScenariosTags'
import { ScenariosSearchSummary } from './components/ScenariosSummary'
import { ScenariosContent } from 'routes/Scenarios/components/ScenariosContent'
import { ScenariosCustomBlock } from './components/ScenariosCustomBlock'
import classes from './Scenarios.module.scss'

export default function ScenariosPage({
  scenarioTemplates,
}: {
  scenarioTemplates: ScenarioTemplateCompact[]
}) {
  return (
    <InnerPageLayout>
      <HeadMetatags title={'Scenarios'} canonical={`/scenarios`} />
      <ScenarioTemplatesProvider scenarioTemplates={scenarioTemplates}>
        <ScenariosPageWithQuery />
      </ScenarioTemplatesProvider>
    </InnerPageLayout>
  )
}

function ScenariosPageWithQuery() {
  const scenarioTemplates = useScenarioTemplates()

  const [queryState, setQueryState] = useQueryStates({
    search: parseAsString.withDefault(''),
    tag: parseAsString.withDefault(''),
    page: parseAsInteger.withDefault(1),
  })

  const { filteredScenarios, visibleScenarios, loading } = useScenarios()

  const tagsData = getScenariosTagsDataWithCount(scenarioTemplates)

  const { search, tag } = queryState

  const handleSearchChange = (search: string) => {
    void setQueryState({
      search,
      tag: null,
      page: 1,
    })
  }

  const handleTagChange = (tag: string) => {
    void setQueryState({
      search: null,
      tag,
      page: 1,
    })
  }

  const handlePageChange = (page: number) => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
    void setQueryState((prevState) => {
      return { ...prevState, page }
    })
  }

  const noFoundScenarios = visibleScenarios.length === 0

  return (
    <Container className={classes.container}>
      <ScenariosHero className={classes.hero} />

      <section className={classes.sidebar}>
        <ScenariosSearch value={search} onChange={handleSearchChange} />
        <ScenariosTags tags={tagsData} value={tag} onChange={handleTagChange} />
      </section>

      <section className={classes.content}>
        <ScenariosSearchSummary
          total={filteredScenarios.length}
          loading={loading}
          isEmptyList={noFoundScenarios}
        />
        <ScenariosContent
          total={filteredScenarios.length}
          loading={loading}
          scenarios={visibleScenarios}
          onPageChange={handlePageChange}
        />
        <ScenariosCustomBlock />
      </section>
    </Container>
  )
}
